import React, { useState } from 'react';
import axios from 'axios';
import { Heart, Send, Loader2, CheckCircle, FileText, Gift, Users, GraduationCap, Building } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { submitToWeb3Forms } from '../lib/forms';
import { getOptimizedImageUrl } from '../lib/utils';

const DonatePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    panNumber: '',
    donationType: '',
    amount: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        organization: formData.organization,
        pan_number: formData.panNumber,
        donation_type: formData.donationType,
        amount: formData.amount,
        message: formData.message
      };

      await submitToWeb3Forms(payload, {
        formName: 'Donation Interest Form',
        subject: `Donation Interest: ${formData.name} (${formData.amount || 'Custom Amount'})`
      });

      toast.success('Thank you for your interest! Our team will contact you shortly.');
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting donation interest:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const impactItems = [
    { icon: GraduationCap, title: "Student Sponsorship", description: "₹50,000 sponsors one student's complete journey to FIRST Global" },
    { icon: Users, title: "Team Support", description: "₹2,50,000 supports an entire team of 5 students" },
    { icon: Gift, title: "Equipment Fund", description: "₹1,00,000 provides robotics kits and materials for training" },
    { icon: Heart, title: "General Fund", description: "Any amount helps us expand STEM education reach" }
  ];

  return (
    <div className="min-h-screen bg-cream" data-testid="donate-page">
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-white">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <img
              src={getOptimizedImageUrl('/assets/images/donate_banner.png', 1920)}
              srcSet={`
                ${getOptimizedImageUrl('/assets/images/donate_banner.png', 800)} 800w,
                ${getOptimizedImageUrl('/assets/images/donate_banner.png', 1200)} 1200w,
                ${getOptimizedImageUrl('/assets/images/donate_banner.png', 1920)} 1920w
              `}
              sizes="100vw"
              alt="Support Our Mission"
              className="w-full h-full object-cover object-bottom opacity-85"
              loading="eager"
            />
            {/* Soft gradient overlay to blend top and bottom */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-cream" />
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-cream via-cream/80 to-transparent" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <div className="bg-cream/75 border border-pragmatic/30 rounded-3xl p-8 md:p-10 shadow-xl shadow-dwarfpony/5 hover:shadow-2xl transition-all duration-300">
                <span className="inline-block text-dwarfpony text-xs font-bold tracking-widest uppercase mb-4 px-4 py-1.5 bg-dwarfpony/10 rounded-full border border-dwarfpony/20">
                  Support Our Mission
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-midnight mb-6 leading-tight tracking-tight">
                  Donate to <span className="block sm:inline text-transparent bg-clip-text bg-gradient-to-r from-dwarfpony to-dwarfpony-dark">STEM Education</span>
                </h1>
                <p className="text-lg md:text-xl text-deepwalnut-dark leading-relaxed font-semibold max-w-2xl mx-auto">
                  Your contribution helps young innovators represent India on the world stage
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="py-16 bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-deepwalnut mb-4">
                Your Impact
              </h2>
              <p className="text-heavyrain max-w-2xl mx-auto">
                Every contribution directly supports students in their journey to global robotics competitions
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {impactItems.map((item, index) => (
                <div
                  key={index}
                  className="bg-cream border border-pragmatic/30 rounded-xl p-6 hover:border-dwarfpony hover:shadow-lg transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pragmatic/30 to-richolive/20 flex items-center justify-center mb-4 group-hover:from-dwarfpony/30 group-hover:to-richolive/30 transition-colors">
                    <item.icon className="w-6 h-6 text-dwarfpony" />
                  </div>
                  <h3 className="text-deepwalnut font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-heavyrain text-sm">{item.description}</p>
                </div>
              ))}
            </div>

            {/* Compliance Documents */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* 12A Registration */}
              <div className="bg-gradient-to-br from-dwarfpony/10 to-pragmatic/10 border border-dwarfpony/20 rounded-xl p-6 flex flex-col sm:flex-row lg:flex-col xl:flex-row items-start gap-4 h-full">
                <div className="w-12 h-12 rounded-xl bg-dwarfpony/20 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-dwarfpony" />
                </div>
                <div className="flex flex-col h-full flex-1">
                  <h3 className="text-deepwalnut font-bold text-lg mb-2">12A Registration</h3>
                  <p className="text-heavyrain text-sm flex-1 mb-4">
                    STEM Education Trust is registered under Section 12A of the Income Tax Act, certifying our status as a tax-exempt charitable trust.
                  </p>
                  <a
                    href="/docs/12AA.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full sm:w-auto px-5 py-2.5 bg-white text-deepwalnut font-semibold rounded-xl border border-pragmatic/30 hover:border-dwarfpony hover:text-dwarfpony hover:shadow-md transition-all text-sm mt-auto"
                  >
                    View 12A Certificate
                  </a>
                </div>
              </div>

              {/* 80G Notice */}
              <div className="bg-gradient-to-br from-richolive/10 to-pragmatic/10 border border-richolive/20 rounded-xl p-6 flex flex-col sm:flex-row lg:flex-col xl:flex-row items-start gap-4 h-full">
                <div className="w-12 h-12 rounded-xl bg-richolive/20 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-richolive" />
                </div>
                <div className="flex flex-col h-full flex-1">
                  <h3 className="text-deepwalnut font-bold text-lg mb-2">80G Tax Benefits</h3>
                  <p className="text-heavyrain text-sm flex-1 mb-4">
                    STEM Education Trust is registered under Section 80G of the Income Tax Act. All donations are eligible for tax deduction.
                  </p>
                  <a
                    href="/docs/80G.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full sm:w-auto px-5 py-2.5 bg-white text-deepwalnut font-semibold rounded-xl border border-pragmatic/30 hover:border-dwarfpony hover:text-dwarfpony hover:shadow-md transition-all text-sm mt-auto"
                  >
                    View 80G Certificate
                  </a>
                </div>
              </div>

              {/* CSR Notice */}
              <div className="bg-gradient-to-br from-pragmatic/10 to-richolive/10 border border-pragmatic/20 rounded-xl p-6 flex flex-col sm:flex-row lg:flex-col xl:flex-row items-start gap-4 h-full">
                <div className="w-12 h-12 rounded-xl bg-pragmatic/20 flex items-center justify-center flex-shrink-0">
                  <Building className="w-6 h-6 text-pragmatic" />
                </div>
                <div className="flex flex-col h-full flex-1">
                  <h3 className="text-deepwalnut font-bold text-lg mb-2">CSR Eligible</h3>
                  <p className="text-heavyrain text-sm flex-1 mb-4">
                    We are eligible to receive Corporate Social Responsibility (CSR) funds to drive large-scale STEM education impact.
                  </p>
                  <a
                    href="/docs/CSR.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full sm:w-auto px-5 py-2.5 bg-white text-deepwalnut font-semibold rounded-xl border border-pragmatic/30 hover:border-dwarfpony hover:text-dwarfpony hover:shadow-md transition-all text-sm mt-auto"
                  >
                    View CSR Certificate
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Donation Interest Form */}
        <section className="py-16 bg-cream-dark">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-cream border border-pragmatic/30 rounded-2xl p-8 shadow-lg">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-richolive/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-richolive" />
                  </div>
                  <h3 className="text-2xl font-bold text-deepwalnut mb-4">Thank You for Your Interest!</h3>
                  <p className="text-heavyrain max-w-md mx-auto mb-6">
                    Our team will contact you within 2 business days with donation details and payment options.
                    You will receive your 80G certificate after the donation is processed.
                  </p>
                  <Button
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({ name: '', email: '', phone: '', organization: '', panNumber: '', donationType: '', amount: '', message: '' });
                    }}
                    className="bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white"
                  >
                    Submit Another Request
                  </Button>
                </div>
              ) : (
                <>
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-deepwalnut mb-2">Express Interest to Donate</h2>
                    <p className="text-heavyrain">Fill the form below and our team will reach out with donation details</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-deepwalnut mb-2">Full Name *</label>
                        <Input
                          type="text"
                          name="name"
                          placeholder="Enter your full name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          data-testid="donate-name-input"
                          className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-deepwalnut mb-2">Email Address *</label>
                        <Input
                          type="email"
                          name="email"
                          placeholder="Enter email address"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          data-testid="donate-email-input"
                          className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-deepwalnut mb-2">Phone Number *</label>
                        <Input
                          type="tel"
                          name="phone"
                          placeholder="Enter phone number"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          data-testid="donate-phone-input"
                          className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-deepwalnut mb-2">Organization (if applicable)</label>
                        <Input
                          type="text"
                          name="organization"
                          placeholder="Company or organization name"
                          value={formData.organization}
                          onChange={handleChange}
                          data-testid="donate-org-input"
                          className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-deepwalnut mb-2">PAN Number (for 80G certificate)</label>
                        <Input
                          type="text"
                          name="panNumber"
                          placeholder="e.g., ABCDE1234F"
                          value={formData.panNumber}
                          onChange={handleChange}
                          data-testid="donate-pan-input"
                          className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony uppercase"
                        />
                        <p className="text-heavyrain text-xs mt-1">Required for tax exemption certificate</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-deepwalnut mb-2">Intended Donation Amount</label>
                        <Input
                          type="text"
                          name="amount"
                          placeholder="e.g., ₹50,000"
                          value={formData.amount}
                          onChange={handleChange}
                          data-testid="donate-amount-input"
                          className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-deepwalnut mb-2">Type of Support *</label>
                      <select
                        name="donationType"
                        value={formData.donationType}
                        onChange={handleChange}
                        required
                        data-testid="donate-type-select"
                        className="w-full h-10 px-3 rounded-md bg-cream-dark border border-pragmatic/30 text-deepwalnut focus:border-dwarfpony focus:outline-none focus:ring-2 focus:ring-dwarfpony/20"
                      >
                        <option value="">Select donation type</option>
                        <option value="student-sponsorship">Student Sponsorship (₹50,000)</option>
                        <option value="team-support">Full Team Support (₹2,50,000)</option>
                        <option value="equipment-fund">Equipment Fund (₹1,00,000)</option>
                        <option value="general-fund">General Fund (Any Amount)</option>
                        <option value="corporate-csr">Corporate CSR Partnership</option>
                        <option value="other">Other / Custom</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-deepwalnut mb-2">Message (Optional)</label>
                      <Textarea
                        name="message"
                        placeholder="Any specific requirements or questions about the donation..."
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        data-testid="donate-message-input"
                        className="bg-cream-dark border-pragmatic/30 text-deepwalnut placeholder:text-heavyrain focus:border-dwarfpony resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="donate-submit-btn"
                      className="w-full bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white font-semibold py-6 text-lg shadow-lg shadow-dwarfpony/25 transition-all"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={20} className="mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Heart size={20} className="mr-2" />
                          Express Interest to Donate
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DonatePage;
